<template>
  <modal-inner class="modal__inner-1--file-properties" aria-label="File properties">
    <div class="modal__content">
      <div class="tabs flex flex--row">
        <tab :active="tab === 'simple'" @click="setSimpleTab()">
          Simple
        </tab>
        <tab :active="tab === 'tags'" @click="tab = 'tags'">
          Tags
        </tab>
        <tab :active="tab === 'yaml'" @click="setYamlTab()">
          YAML
        </tab>
      </div>
      <div v-if="tab === 'simple'">
        <div class="modal__title">Extensions</div>
        <div class="modal__sub-title">Configure the Markdown engine.</div>
        <form-entry label="Preset">
          <select slot="field" class="textfield" v-model="preset" @keydown.enter="resolve()">
            <option v-for="(preset, id) in presets" :key="id" :value="preset">
              {{ preset }}
            </option>
          </select>
        </form-entry>
      </div>
      <div v-else-if="tab === 'tags'" class="tags-panel">
        <div class="modal__title">Tags</div>
        <div class="modal__sub-title">Add tags to organize your notes.</div>
        <div class="tags-input-container">
          <input
            type="text"
            class="textfield tags-input"
            v-model="newTag"
            @keydown.enter="addTag"
            placeholder="Add a tag..."
          >
          <button class="button" @click="addTag" :disabled="!newTag.trim()">Add</button>
        </div>
        <div class="tags-list" v-if="fileTags.length">
          <span
            v-for="(tag, index) in fileTags"
            :key="index"
            class="tag-chip"
          >
            {{ tag }}
            <button class="tag-chip__remove" @click="removeTag(index)">&times;</button>
          </span>
        </div>
        <div class="tags-empty" v-else>No tags added yet.</div>
        <div class="tags-suggestions" v-if="suggestedTags.length">
          <div class="tags-suggestions__title">Suggested tags:</div>
          <span
            v-for="tag in suggestedTags"
            :key="tag"
            class="tag-chip tag-chip--suggestion"
            @click="addSuggestedTag(tag)"
          >
            {{ tag }}
          </span>
        </div>
      </div>
      <div v-if="tab === 'yaml'">
        <div class="form-entry" role="tabpanel" aria-label="YAML properties">
          <label class="form-entry__label">YAML</label>
          <div class="form-entry__field">
            <code-editor lang="yaml" :value="yamlProperties" key="custom-properties" @changed="setYamlProperties"></code-editor>
          </div>
        </div>
        <div class="modal__error modal__error--file-properties">{{error}}</div>
        <div class="modal__info modal__info--multiline">
          <p><strong>ProTip:</strong> You can manually toggle extensions:</p>
          <pre class=" language-yaml"><code class="prism  language-yaml"><span class="token key atrule">extensions</span><span class="token punctuation">:</span>
  <span class="token key atrule">emoji</span><span class="token punctuation">:</span>
    <span class="token comment"># Enable emoji shortcuts like :) :-(</span>
    <span class="token key atrule">shortcuts</span><span class="token punctuation">:</span> <span class="token boolean important">true</span>
</code></pre>
          <p>Use preset <code>zero</code> to make your own configuration:</p>
          <pre class=" language-yaml"><code class="prism  language-yaml"><span class="token key atrule">extensions</span><span class="token punctuation">:</span>
  <span class="token key atrule">preset</span><span class="token punctuation">:</span> zero
  <span class="token key atrule">markdown</span><span class="token punctuation">:</span>
    <span class="token key atrule">table</span><span class="token punctuation">:</span> <span class="token boolean important">true</span>
  <span class="token key atrule">katex</span><span class="token punctuation">:</span>
    <span class="token key atrule">enabled</span><span class="token punctuation">:</span> <span class="token boolean important">true</span>
</code></pre>
        </div>
      </div>
    </div>
  </modal-inner>
</template>

<script>
import yaml from 'js-yaml';
import { mapGetters } from 'vuex';
import ModalInner from './common/ModalInner.vue';
import Tab from './common/Tab.vue';
import FormEntry from './common/FormEntry.vue';
import CodeEditor from '../CodeEditor.vue';
import utils from '../../services/utils';
import presets from '../../data/presets';
import store from '../../store';


export default {
  components: {
    ModalInner,
    Tab,
    FormEntry,
    CodeEditor,
  },
  data: () => ({
    contentId: null,
    fileId: null,
    yamlProperties: null,
    preset: '',
    error: null,
    newTag: '',
    fileTags: [],
  }),
  computed: {
    ...mapGetters('modal', [
      'config',
    ]),
    presets: () => Object.keys(presets).sort(),
    tab: {
      get() {
        return store.getters['data/localSettings'].filePropertiesTab || 'simple';
      },
      set(value) {
        store.dispatch('data/patchLocalSettings', {
          filePropertiesTab: value,
        });
      },
    },
    allTags() {
      const tags = new Set();
      store.getters['file/items'].forEach((file) => {
        if (file.tags && Array.isArray(file.tags)) {
          file.tags.forEach(tag => tags.add(tag));
        }
      });
      return Array.from(tags).sort();
    },
    suggestedTags() {
      return this.allTags.filter(tag => !this.fileTags.includes(tag)).slice(0, 5);
    },
  },
  watch: {
    preset() {
      this.saveChanges();
    },
    fileTags: {
      deep: true,
      handler() {
        this.saveTagsImmediately();
      },
    },
    yamlProperties() {
      this.saveChanges();
    },
  },
  created() {
    const content = store.getters['content/current'];
    const file = store.getters['file/current'];
    this.contentId = content.id;
    this.fileId = file.id;
    this.fileTags = [...(file.tags || [])];
    this.setYamlProperties(content.properties);
    if (this.tab !== 'yaml' && this.tab !== 'tags') {
      this.setSimpleTab();
    }
  },
  methods: {
    yamlToSimple() {
      const properties = this.properties || {};
      const extensions = properties.extensions || {};
      this.preset = extensions.preset;
      if (!this.presets.includes(this.preset)) {
        this.preset = 'default';
      }
    },
    simpleToYaml() {
      let hasChanged = false;
      const properties = this.properties || {};
      const extensions = properties.extensions || {};
      if (this.preset !== extensions.preset) {
        if (this.preset !== 'default') {
          extensions.preset = this.preset;
          hasChanged = true;
        } else if (extensions.preset) {
          delete extensions.preset;
          hasChanged = true;
        }
      }
      if (hasChanged) {
        if (Object.keys(extensions).length) {
          properties.extensions = extensions;
        } else {
          delete properties.extensions;
        }
        this.setYamlProperties(Object.keys(properties).length
          ? yaml.dump(properties)
          : '\n');
      }
    },
    setSimpleTab() {
      this.tab = 'simple';
      this.yamlToSimple();
    },
    setYamlTab() {
      this.tab = 'yaml';
      this.simpleToYaml();
    },
    setYamlProperties(value) {
      this.yamlProperties = value;
      try {
        this.properties = yaml.load(value);
        this.error = null;
      } catch (e) {
        this.error = e.message;
      }
    },
    addTag() {
      const tag = this.newTag.trim().toLowerCase();
      if (tag && !this.fileTags.includes(tag)) {
        this.fileTags.push(tag);
      }
      this.newTag = '';
    },
    removeTag(index) {
      this.fileTags.splice(index, 1);
    },
    addSuggestedTag(tag) {
      if (!this.fileTags.includes(tag)) {
        this.fileTags.push(tag);
      }
    },
    saveChanges() {
      if (this.tab === 'simple') {
        this.simpleToYaml();
      }
      if (!this.error) {
        store.commit('content/patchItem', {
          id: this.contentId,
          properties: utils.sanitizeText(this.yamlProperties),
        });
      }
    },
    saveTagsImmediately() {
      store.commit('file/patchItem', {
        id: this.fileId,
        tags: this.fileTags,
      });
    },
  },
};
</script>

<style lang="scss">
@import '../../styles/variables.scss';

.modal__inner-1.modal__inner-1--file-properties {
  max-width: 520px;
}

.modal__error--file-properties {
  white-space: pre-wrap;
  font-family: $font-family-monospace;
  font-size: $font-size-monospace;
}

.tags-panel {
  padding: 10px 0;
}

.tags-input-container {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;

  .tags-input {
    flex: 1;
  }
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.tags-empty {
  color: rgba(0, 0, 0, 0.5);
  font-size: 14px;
  margin-bottom: 16px;

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    color: rgba(255, 255, 255, 0.5);
  }

  .app--sepia & {
    color: rgba(91, 70, 54, 0.6);
  }

  .app--solarized-light & {
    color: rgba(88, 110, 117, 0.6);
  }
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  font-size: 13px;

  .app--dark & {
    background: rgba(255, 255, 255, 0.15);
  }

  &--suggestion {
    cursor: pointer;
    opacity: 0.7;

    &:hover {
      opacity: 1;
      background: rgba(0, 0, 0, 0.12);

      .app--dark & {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }
}

.tag-chip__remove {
  background: none;
  border: none;
  padding: 0;
  margin-left: 2px;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.5);

  &:hover {
    color: rgba(0, 0, 0, 0.8);
  }

  .app--dark & {
    color: rgba(255, 255, 255, 0.5);

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }
}

.tags-suggestions {
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  .app--dark & {
    border-top-color: rgba(255, 255, 255, 0.1);
  }
}

.tags-suggestions__title {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 8px;

  .app--dark & {
    color: rgba(255, 255, 255, 0.5);
  }
}
</style>
